import { cbSemanticCssVars, colors } from '@creditornot/cb-ingredients';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { OfflineTimeInsight } from 'apis/merchant-gateway/insights';
import { Legend, LegendChartType, VerticalBarChart } from 'modules/experience-core';
import { formatGrowthRate, formatMinutes, getGrowthRateForFields, getGrowthTrend } from 'modules/insights-v2/utils';

import { InsightResponseWithPreviousInterval } from '../../types';
import ErrorView from '../ErrorView';
import LoadingView from '../LoadingView';
import MetricBlock from '../OverallPerformance/MetricBlock';

type OfflineTimeProps = {
  offlineTimeInsight: InsightResponseWithPreviousInterval<OfflineTimeInsight>;
};

const OfflineTimeContainer = styled.div`
  position: relative;
  border: 1px solid ${cbSemanticCssVars.borderPrimary};
  border-radius: 8px;

  min-height: 373px;

  display: flex;
  flex-direction: column;
`;

const OfflineTimeSummaryContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding-block: 16px;
  padding-inline: 24px;
`;

const Divider = styled.div`
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const OfflineMinuteChartContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  margin: 40px 16px 0;

  width: 100%;
`;

const OfflineMinuteContent = styled.div`
  display: flex;
`;

enum OfflineTimeCategory {
  OFFLINE_MINUTES = 'offlineMinutes',
}

const DEFAULT_OFFLINE_TOTAL = {
  openingMinutes: 0,
  offlineMinutes: 0,
};

const OfflineTimeLegacy = ({ offlineTimeInsight }: OfflineTimeProps) => {
  const offlineTimeTotalForSelected =
    offlineTimeInsight.selected.data?.data.offlineTime?.total ?? DEFAULT_OFFLINE_TOTAL;
  const offlineTimeTotalForPrevious =
    offlineTimeInsight.previous.data?.data.offlineTime?.total ?? DEFAULT_OFFLINE_TOTAL;
  const data = offlineTimeInsight.selected.data?.data.offlineTime?.split;

  const { formatMessage, locale } = useIntl();

  const offlineTimeLegends: Record<OfflineTimeCategory, Legend> = {
    [OfflineTimeCategory.OFFLINE_MINUTES]: {
      value: formatMessage({ id: 'insights.operations.offline-time' }),
      color: colors.wolt88,
      type: LegendChartType.BAR,
    },
  };

  const { offlineMinutes: offlineMinutesGrowth } = getGrowthRateForFields(
    offlineTimeTotalForSelected,
    offlineTimeTotalForPrevious,
    ['offlineMinutes'],
  );

  const chartData = useMemo(() => {
    return (data ?? []).map(item => {
      const { label, total } = item;
      const { offlineMinutes } = total;
      return { offlineMinutes, name: label || '' };
    });
  }, [data]);

  return (
    <OfflineTimeContainer>
      {offlineTimeInsight.isFetching ? (
        <LoadingView />
      ) : offlineTimeInsight.isError ? (
        <ErrorView onRetry={offlineTimeInsight.refetch} />
      ) : (
        <>
          <OfflineTimeSummaryContainer>
            <MetricBlock
              title={formatMessage({ id: 'insights.offline-time.header' })}
              tooltip={formatMessage({ id: 'insights.offline-time.tooltip' })}
              metrics={formatMinutes(offlineTimeTotalForSelected.offlineMinutes)}
              growth={formatGrowthRate(offlineMinutesGrowth, locale)}
              growthTrend={getGrowthTrend(offlineMinutesGrowth, true)}
            />
          </OfflineTimeSummaryContainer>
          <Divider />
          <OfflineMinuteContent>
            <OfflineMinuteChartContainer>
              <VerticalBarChart
                legends={offlineTimeLegends}
                isStacked
                data={chartData}
                valueFormatter={formatMinutes}
              />
            </OfflineMinuteChartContainer>
          </OfflineMinuteContent>
        </>
      )}
    </OfflineTimeContainer>
  );
};

export default OfflineTimeLegacy;
